import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ArrowLeft } from 'lucide-react';
import SettingsMenu from '../components/Settings/SettingsMenu';
import ProfileAccountSection from '../components/Settings/ProfileAccount';
import PrivacySection from '../components/Settings/Privacy';
import ActivitySection from '../components/Settings/Activity';
import CollectionsSection from '../components/Settings/Collections';
import AccountPrivacySection from '../components/Settings/AccountPrivacy';
import BlockedAccountsSection from '../components/Settings/BlockedAccounts';
import DeleteAccountSection from '../components/Settings/DeleteAccount';
import AboutSection from '../components/Settings/About';
import PrivacyPolicySection from '../components/Settings/PrivacyPolicy';
import TermsOfUseSection from '../components/Settings/TermsOfUse';
import CookiesPolicySection from '../components/Settings/CookiesPolicy';

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('profile');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 800;
      setIsMobile(mobile);
      if (!mobile) setShowContent(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleBackToMain = () => {
    setShowContent(false);
  };

  const needsResponsiveContainer = (section) => {
    return ['activity', 'collections', 'archive', 'about', 'privacy-policy', 'terms-of-use', 'cookies-policy', 'blocked-accounts'].includes(section);
  };

  const renderSection = () => {
    const sectionContent = (() => {
      switch(activeSection) {
        case 'profile': return <ProfileAccountSection />;
        case 'delete-account': return <DeleteAccountSection onBack={handleBackToMain} />;
        case 'account-privacy': return <AccountPrivacySection />;
        case 'privacy': return <PrivacySection />;
        case 'activity': return <ActivitySection onBack={handleBackToMain} />;
        case 'collections': return <CollectionsSection onBack={handleBackToMain} />;
        case 'blocked-accounts': return <BlockedAccountsSection />; 
        case 'about': return <AboutSection onBack={handleBackToMain} />;
        case 'privacy-policy': return <PrivacyPolicySection onBack={handleBackToMain} />;
        case 'terms-of-use': return <TermsOfUseSection onBack={handleBackToMain} />;
        case 'cookies-policy': return <CookiesPolicySection onBack={handleBackToMain} />;
        default: return <ProfileAccountSection />;
      }
    })();

    if (needsResponsiveContainer(activeSection)) {
      return (
        <div className="flex-1 min-w-0">
          {sectionContent}
        </div>
      );
    }

    return sectionContent;
  };

  if (isMobile) {
    return (
      <div className="min-h-screen bg-white">
        {!showContent ? (
          <div>
            <div className="bg-white px-4 py-4 sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleGoBack}
                  className="text-black"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-6 h-6 text-black" />
                </button>
                <h1 className="text-xl font-semibold text-black">Settings</h1>
              </div>
            </div>
            <SettingsMenu 
              activeSection={activeSection}
              onSectionChange={(section) => {
                setActiveSection(section);
                setShowContent(true);
              }}
              isMobile={isMobile}
            />
          </div>
        ) : (
          <div>
            <div className="bg-white px-4 py-4 sticky top-0 z-10">
              <div className="flex items-center">
                <button
                  onClick={() => setShowContent(false)}
                  className="mr-3 p-1 hover:bg-gray-100 rounded-full"
                >
                  <ChevronLeft className="w-6 h-6 text-black" />
                </button>
                <h1 className="text-lg font-semibold text-black">
                  {getSectionTitle(activeSection)}
                </h1>
              </div>
            </div>

            <div className="p-0">
              {renderSection()}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={handleGoBack}
              className="text-black p-2"
              aria-label="Go back"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold text-black">Account Settings</h1>
          </div>
          <p className="text-black text-sm ml-14">Manage your profile, privacy, and account settings</p>
        </div>

        <div className="flex gap-6">
          <div className="w-80 flex-shrink-0">
            <SettingsMenu 
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              isMobile={isMobile}
            />
          </div>

          <div className={`${needsResponsiveContainer(activeSection) ? 'flex-1 min-w-0' : 'flex-1'}`}>
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

const getSectionTitle = (section) => {
  const titles = {
    profile: 'Profile & Account',
    'account-privacy': 'Account Privacy',
    privacy: 'Privacy & Interactions',
    notifications: 'Notifications',
    activity: 'Your Activity',
    collections: 'Collections',
    'blocked-accounts': 'Blocked Accounts',
    archive: 'Archive',
    about: 'About',
    'privacy-policy': 'Privacy Policy',
    'terms-of-use': 'Terms of Use',
    'cookies-policy': 'Cookies Policy',
    preferences: 'App Preferences'
  };
  return titles[section] || 'Settings';
};

export default Settings;